import React, { useEffect } from 'react';
import {
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleMouseWheel,
} from '../Renderers/vtkManagers';
import type { CPRContext } from '../Renderers/vtkManagers';

export function useCprInteractionEvents({
  ref,
  context,
  analysisType,
  panelToolState,
  currentVOI,
  updateWindowLevelWidth,
  config,
  onCprOverlayChange,
  disabled = false,
}: {
  ref: React.RefObject<HTMLDivElement>;
  context: React.MutableRefObject<CPRContext | null>;
  analysisType: string;
  panelToolState: any;
  currentVOI: any;
  updateWindowLevelWidth: (...args: any[]) => void;
  config: any;
  onCprOverlayChange?: () => void;
  disabled?: boolean;
}) {
  useEffect(() => {
    if (disabled) return;
    if (!context.current || !ref.current) return;
    if (!panelToolState) return;

    const container = ref.current;
    const shouldBypassForBoundaryEdit = (evt: MouseEvent) => {
      return !!(window as any).__CPR_BOUNDARY_EDITING__ || !!evt.altKey;
    };

    const wheelHandler = (evt: WheelEvent) => {
      handleMouseWheel(context, evt);
      onCprOverlayChange?.();
    };
    const mouseMoveHandler = (evt: MouseEvent) => {
      if (shouldBypassForBoundaryEdit(evt)) return;
      handleMouseMove({
        context,
        evt,
        toolName: panelToolState.activeToolId,
        voi: currentVOI,
        updateWindowLevelWidth,
      });
      if (context.current?.dragState?.isDragging) {
        onCprOverlayChange?.();
      }
    };
    const mouseDownHandler = (evt: MouseEvent) => {
      if (shouldBypassForBoundaryEdit(evt)) return;
      handleMouseDown(context, evt);
    };
    const mouseUpHandler = (evt: MouseEvent) => {
      if (shouldBypassForBoundaryEdit(evt)) return;
      handleMouseUp(context, evt);
    };

    container.addEventListener('wheel', wheelHandler);
    container.addEventListener('mousedown', mouseDownHandler);
    container.addEventListener('mouseup', mouseUpHandler);
    container.addEventListener('mousemove', mouseMoveHandler);

    return () => {
      container.removeEventListener('wheel', wheelHandler);
      container.removeEventListener('mousedown', mouseDownHandler);
      container.removeEventListener('mouseup', mouseUpHandler);
      container.removeEventListener('mousemove', mouseMoveHandler);
    };
  }, [
    context,
    ref,
    analysisType,
    panelToolState,
    updateWindowLevelWidth,
    currentVOI,
    config,
    onCprOverlayChange,
    disabled,
  ]);
}
